  // Libraries
import React from 'react'
import ReactDOM from 'react-dom'
import applyApiFix from './utils/patches/apiFix'
import applyTypeaheadFix from './utils/patches/typeaheadFix'
import applyDebugLogger from './utils/patches/debugLogger'
import applyPaymentMethodsFixAggressive from './utils/patches/paymentMethodsFixAggressive'
import applyPaymentMethodsTransform from './utils/patches/paymentMethodsTransform'
import applyTypeaheadFixDefensive from './utils/patches/typeaheadFixDefensive'

// Apply patches immediately
applyDebugLogger();
applyApiFix();
applyPaymentMethodsFixAggressive();
applyPaymentMethodsTransform();
applyTypeaheadFix();
applyTypeaheadFixDefensive();

import {
  Router,
  Route,
  IndexRoute,
  browserHistory
} from 'react-router'
import { Provider } from 'mobx-react'
import {StripeProvider} from 'react-stripe-elements';
import Environment from './utils/environment'

// Apollo imports
import { ApolloClient } from 'apollo-client';
import { ApolloProvider } from 'react-apollo';
import { setContext } from 'apollo-link-context';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';

let stripeKey = Environment.setStripeKey()

// Components
import Accessories from './components/admin/accessories'
import Admin from './components/admin'
import AdminOrganizations from './components/admin/organizations'
import AdminEditOrg from './components/admin/editorg'
import AdminUsers from './components/admin/users'
import AdminPermissions from './components/admin/permissions'
import AdminSiteOptions from './components/admin/siteoptions'
import AssetManager from './components/assetmanager';
import Backends from './components/admin/backends'
import Carriers from './components/admin/carriers'
import Categories from './components/admin/categories'
import Company from './components/company'
import CompanyDetails from './components/company/details'
import CompanyCreateInvoice from './components/company/createinvoice'
import CompanyEditInvoice from './components/company/editinvoice'
import CompanyInvoices from './components/company/invoices'
import CompanyOrders from './components/company/orders'
import CompanyQuotes from './components/company/quotes'
import CompanyStatus from './components/admin/companystates'
import Companies from './components/companies'
import Connect from './components/quickbooks/connect'
import Contacts from './components/companies/contacts'
import Customers from './components/companies/customers'
import Dashboard from './components/dashboard'
import Features from './components/admin/features'
import ForgotPassword from './components/forgotpassword'
import Hardware from './components/admin/hardware'
import Service from './components/admin/service'
import Imports from './components/admin/imports'
import Inactive from './components/companies/inactive'
import Invoice from'./components/invoice'
import InvoiceView from './components/invoices/invoiceview'
import Invoices from './components/invoices'
import Leads from './components/companies/leads'
import Login from './components/login'
import Logout from './components/logout'
import Main from './Main'
import Order from './components/order'
import Orders from './components/orders'
import Organizations from './components/admin/organizations'
import PackingSlip from './components/packingslip'
import Partner from './components/partner'
import Partners from './components/partners'
import PartnerTasks from './components/partnertasks'
import PartnerTypes from './components/admin/partnertypes'
import PasswordReset from './components/passwordreset'
import PaymentConfirmation from './components/invoices/paymentconfirmation'
import PaymentIntervals from './components/admin/paymentintervals'
import ProcessPayment from './components/invoices/processpayment'
import Profile from './components/admin/profile'
import Quickbooks from './components/quickbooks/quickbooks'
import Quote from './components/quote'
import Quotes from './components/quotes'
import Reports from './components/reports'
import ShippingTypes from './components/admin/shippingtypes'
import Sources from './components/admin/sources'
import stores from './stores/index'
import Tasks from './components/tasks'
import UpdateIntervals from './components/admin/updateintervals'
import Vendors from './components/admin/vendors'

import Config from './config';

/**
 * Apollo Connection
 */
const httpLink = createHttpLink({
  uri: Config.graphql_url,
  opts: {
    mode: 'no-cors',
  }
});
const authLink = setContext((_, { headers }) => {
  if (localStorage.getItem('GPSLeadersAssetManagerAuthToken') === null) {
    localStorage.setItem('GPSLeadersAssetManagerAuthToken', '');
  }
  const token = localStorage.getItem('GPSLeadersAssetManagerAuthToken');
  return {
    headers: {
      Authorization: token,
      ...headers,
    },
  };
});
const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only'
    }
  }
});
/**
 * END OF Apollo Connection
 */

const router = (
  <ApolloProvider client={client}>
    <StripeProvider apiKey={ stripeKey }>
      <Provider
        accessories={ stores.accessories }
        activities={ stores.activities}
        agreements={ stores.agreements }
        addresses={ stores.addresses }
        backends={ stores.backends }
        billingstatuses={ stores.billingstatuses }
        carriers={ stores.carriers }
        categories={ stores.categories }
        companies={ stores.companies }
        companystates={ stores.companystates }
        contacts={ stores.contacts }
        features={ stores.features }
        hardware={ stores.hardware }
        service={ stores.service }
        industries={ stores.industries }
        invoices={ stores.invoices }
        globals={ stores.globals }
        mailchimp={ stores.mailchimp }
        organizations={ stores.organizations }
        orders={ stores.orders }
        partnertypes={ stores.partnertypes }
        payments={ stores.payments }
        paymentintervals={ stores.paymentintervals }
        quickbooks={ stores.quickbooks }
        quotes={ stores.quotes }
        shippingtypes={ stores.shippingtypes }
        sources={ stores.sources }
        terms={ stores.terms }
        updateintervals={ stores.updateintervals }
        users={ stores.users }
        partners={ stores.partners }
        vendors={ stores.vendors }
        tasks={ stores.tasks }
      >
        <Router history={ browserHistory }>
          <Route path="/" component={ Main }>
            <IndexRoute component={ Dashboard } />
            <Route path="/admin/imports" component={ Imports } />
            <Route path="/admin/organizations" component={ AdminOrganizations } />
            <Route path="/admin/organizations/:organizationId" component={ AdminOrganizations } />
            <Route path="/admin/permissions" component={ AdminPermissions } />
            <Route path="/admin/profile" component={ Profile } />
            <Route path="/admin/siteoptions" component={ AdminSiteOptions } />
            <Route path="/admin/users" component={ AdminUsers } />
            <Route path="/assetmanager" component={ AssetManager } />
            <Route path="/assetmanager/:compid" component={ AssetManager } />
            <Route path="/company/details" component={ CompanyDetails } />
            <Route path="/company/details/:companyId" component={ CompanyDetails } />
            <Route path="/company/details/:companyId/:type/:typeId" component={ CompanyDetails } />
            <Route path="/company/createinvoice" component={ CompanyCreateInvoice } />
            <Route path="/company/createinvoice/:companyId" component={ CompanyCreateInvoice } />
            <Route path="/company/editinvoice" component={ CompanyEditInvoice } />
            <Route path="/company/editinvoice/:companyId/:invoiceId" component={ CompanyEditInvoice } />
            <Route path="/company/invoices/:companyId" component={ CompanyInvoices } />
            <Route path="/companies" component={ Companies } />
            <Route path="/contacts" component={ Contacts } />
            <Route path="/customers" component={ Customers } />
            <Route path="/forgotpassword" component={ ForgotPassword } />
            <Route path="/inactive" component={ Inactive } />
            <Route path="/invoice/:invoiceId" component={ Invoice } />
            <Route path="/invoiceview/:token" component={ InvoiceView } />
            <Route path="/invoices" component={ Invoices } />
            <Route path="/leads" component={ Leads } />
            <Route path="/login" component={ Login } />
            <Route path="/logout" component={ Logout } />
            <Route path="/order/:orderId" component={ Order } />
            <Route path="/orders" component={ Orders } />
            <Route path="/packingslip/:quoteId" component={ PackingSlip } />
            <Route path="/partner/:partnerId" component={ Partner } />
            <Route path="/partner/:partnerId/:type/:typeId" component={ Partner } />
            <Route path="/partners" component={ Partners } />
            <Route path="/partnertasks" component={ PartnerTasks } />
            <Route path="/passwordreset/:resetToken" component={ PasswordReset } />
            <Route path="/paymentconfirmation" component={ PaymentConfirmation } />
            <Route path="/processpayment/:token" component={ ProcessPayment } />
            <Route path="/quickbooks" component={ Quickbooks } />
            <Route path="/quickbooks/connect" component={ Connect } />
            <Route path="/reports" component={ Reports } />
            <Route path="/reports/leadcountreport/:startDate/:endDate" component={ Reports } />
            <Route path="/tasks" component={ Tasks } />
          </Route>
        </Router>
      </Provider>
    </StripeProvider>
  </ApolloProvider>
)

ReactDOM.render(
  router,
  document.getElementById('root')
)



// WEBPACK FOOTER //
// ./src/index.js